import {useTheme} from "styled-components";
import {brand} from "theme/tokens";

/**
 * The four step illustrations on `/advertising` (ui#1861).
 *
 * Inline SVG rather than images: they are four small diagrams, they must repaint with the theme, and a
 * `<img>` per step would be four network round trips on a pre-rendered marketing page for drawings that
 * weigh a few hundred bytes each.
 *
 * Every one is `aria-hidden` and `focusable="false"`. They carry NO information the step's own heading
 * and line do not already carry, and a decorative diagram announced as an image is noise in a list a
 * screen reader is already reading in order.
 *
 * Colours: the brand accents for the marks that are meant to be vivid (a grid of coloured plates, an
 * upload badge, a paid tick) and `theme.tokens` reads for anything that has to sit on the card surface
 * behind them. The vivid accents come from `brand`, which is mode-independent by design — these are the
 * SAME hues the placeholder plates and the file chips use, and a diagram of "your images" that changed
 * palette in dark mode would stop being a diagram of the same thing.
 */

/** One drawing, keyed by the step it illustrates. */
export type AdvertisingStepArtKey = "look" | "art" | "dates" | "pay";

export function AdvertisingStepArt({step}: {step: AdvertisingStepArtKey}) {
	const {tokens} = useTheme();
	const surface = tokens.card;
	const outline = tokens.cardBorder;
	const ink = tokens.ink;
	const faint = tokens.ink3;

	if (step === "look") {
		// Six plates in the picker's own grid, one of them ringed as the chosen look.
		return (
			<svg width="96" height="64" viewBox="0 0 96 64" fill="none" aria-hidden="true" focusable="false">
				<rect x="2" y="2" width="28" height="28" rx="6" fill={tokens.blue}/>
				<rect x="34" y="2" width="28" height="28" rx="6" fill={tokens.teal}/>
				<rect x="66" y="2" width="28" height="28" rx="6" fill={brand.gold}/>
				<rect x="2" y="34" width="28" height="28" rx="6" fill={tokens.violet} opacity="0.7"/>
				<rect x="34" y="34" width="28" height="28" rx="6" fill={brand.sky} opacity="0.7"/>
				<rect x="66" y="34" width="28" height="28" rx="6" fill={tokens.orange} opacity="0.7"/>
				<rect x="1" y="1" width="30" height="30" rx="7" stroke={ink} strokeWidth="2"/>
			</svg>
		);
	}

	if (step === "art") {
		// A picture plate with an add badge — the creative step's own "Add images" control, in miniature.
		return (
			<svg width="96" height="64" viewBox="0 0 96 64" fill="none" aria-hidden="true" focusable="false">
				<rect x="2" y="2" width="60" height="60" rx="8" fill={surface} stroke={outline} strokeWidth="2"/>
				<circle cx="20" cy="22" r="6" fill={brand.gold}/>
				<path d="M6 54 L24 34 L36 46 L44 38 L58 54Z" fill={tokens.teal}/>
				<circle cx="76" cy="46" r="16" fill={tokens.blue}/>
				<path d="M76 38v16M68 46h16" stroke={tokens.blueInk} strokeWidth="3" strokeLinecap="round"/>
			</svg>
		);
	}

	if (step === "dates") {
		// Two rows of a calendar with a run of three consecutive days taken, which is what a booking is.
		return (
			<svg width="120" height="52" viewBox="0 0 120 52" fill="none" aria-hidden="true" focusable="false">
				<g stroke={outline} strokeWidth="2" fill={surface}>
					<rect x="1" y="1" width="26" height="26" rx="6"/>
					<rect x="31" y="1" width="26" height="26" rx="6"/>
					<rect x="61" y="1" width="26" height="26" rx="6"/>
					<rect x="91" y="1" width="26" height="26" rx="6"/>
					<rect x="1" y="31" width="26" height="20" rx="6"/>
					<rect x="31" y="31" width="26" height="20" rx="6"/>
					<rect x="61" y="31" width="26" height="20" rx="6"/>
					<rect x="91" y="31" width="26" height="20" rx="6"/>
				</g>
				<rect x="31" y="1" width="26" height="26" rx="6" fill={tokens.blue}/>
				<rect x="61" y="1" width="26" height="26" rx="6" fill={tokens.blue}/>
				<rect x="91" y="1" width="26" height="26" rx="6" fill={tokens.blue}/>
				<rect x="61" y="31" width="26" height="20" rx="6" fill={tokens.chip}/>
			</svg>
		);
	}

	// A receipt with a paid tick: one payment, and a person's approval after it.
	return (
		<svg width="80" height="64" viewBox="0 0 80 64" fill="none" aria-hidden="true" focusable="false">
			<rect x="8" y="2" width="52" height="60" rx="6" fill={surface} stroke={outline} strokeWidth="2"/>
			<path d="M18 18h32M18 28h32M18 38h20" stroke={faint} strokeWidth="3" strokeLinecap="round"/>
			<circle cx="62" cy="48" r="14" fill={tokens.teal}/>
			<path
				d="M55 48l5 5 9-10"
				stroke={surface}
				strokeWidth="3"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/** The order the four drawings are used in, matching the copy module's four steps. */
export const ADVERTISING_STEP_ART: readonly AdvertisingStepArtKey[] = ["look", "art", "dates", "pay"];
